import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ServiceConfigurator = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({
    maturity: '',
    size: '',
    goals: '',
    timeline: ''
  });
  const [recommendation, setRecommendation] = useState(null);

  const steps = [
    {
      title: "AI Maturity Level",
      key: "maturity",
      options: [
        { value: 'exploring', label: 'Exploring AI Opportunities', description: 'Just starting to understand AI potential' },
        { value: 'piloting', label: 'Running AI Pilots', description: 'Testing AI solutions in limited scope' },
        { value: 'scaling', label: 'Scaling AI Initiatives', description: 'Expanding successful AI implementations' },
        { value: 'optimizing', label: 'Optimizing AI Operations', description: 'Fine-tuning existing AI systems' }
      ]
    },
    {
      title: "Organization Size",
      key: "size",
      options: [
        { value: 'startup', label: 'Startup (1-50 employees)', description: 'Agile team, rapid iteration needs' },
        { value: 'growth', label: 'Growth Stage (51-500 employees)', description: 'Scaling operations, structured processes' },
        { value: 'enterprise', label: 'Enterprise (500+ employees)', description: 'Complex systems, compliance requirements' },
        { value: 'fortune', label: 'Fortune 500', description: 'Multi-division, global operations' }
      ]
    },
    {
      title: "Primary Goals",
      key: "goals",
      options: [
        { value: 'strategy', label: 'Strategic Planning', description: 'Define AI roadmap and governance' },
        { value: 'implementation', label: 'Technical Implementation', description: 'Build and deploy AI solutions' },
        { value: 'optimization', label: 'Performance Optimization', description: 'Improve existing AI systems' },
        { value: 'transformation', label: 'Digital Transformation', description: 'Organization-wide AI adoption' }
      ]
    },
    {
      title: "Timeline Expectations",
      key: "timeline",
      options: [
        { value: 'immediate', label: 'Immediate (1-3 months)', description: 'Urgent business needs' },
        { value: 'quarter', label: 'This Quarter (3-6 months)', description: 'Planned initiative launch' },
        { value: 'year', label: 'This Year (6-12 months)', description: 'Strategic implementation' },
        { value: 'longterm', label: 'Long-term (12+ months)', description: 'Comprehensive transformation' }
      ]
    }
  ];

  const getRecommendation = (selections) => {
    const { maturity, size, goals, timeline } = selections;
    
    if (maturity === 'exploring' || goals === 'strategy') {
      return {
        service: 'Strategic Assessment',
        reason: 'Perfect for organizations defining their AI strategy and understanding implementation pathways.',
        nextSteps: ['Executive briefing session', 'AI readiness assessment', 'Strategic roadmap development']
      };
    } else if (goals === 'implementation' || maturity === 'piloting') {
      return {
        service: 'Technical Implementation',
        reason: 'Ideal for hands-on development and deployment of AI solutions with your team.',
        nextSteps: ['Technical architecture review', 'Implementation planning', 'Development sprint planning']
      };
    } else {
      return {
        service: 'Ongoing Optimization',
        reason: 'Best suited for organizations looking to enhance and scale existing AI capabilities.',
        nextSteps: ['Performance audit', 'Optimization roadmap', 'Continuous improvement setup']
      };
    }
  };

  const handleNext = () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const rec = getRecommendation(selections);
      setRecommendation(rec);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setSelections({ maturity: '', size: '', goals: '', timeline: '' });
    setRecommendation(null);
  };

  const currentStepData = steps?.[currentStep];
  const isStepComplete = selections?.[currentStepData?.key];
  const allStepsComplete = Object.values(selections)?.every(value => value !== '');

  if (recommendation) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Target" size={32} color="white" />
          </div>
          <h3 className="text-2xl font-bold text-text-primary mb-2">
            Recommended Service
          </h3>
          <p className="text-text-secondary">
            Based on your responses, here's our recommendation
          </p>
        </div>
        <div className="bg-brand-surface rounded-lg p-6 mb-6">
          <h4 className="text-xl font-bold text-brand-primary mb-3">
            {recommendation?.service}
          </h4>
          <p className="text-text-secondary mb-4">
            {recommendation?.reason}
          </p>
          
          <div>
            <h5 className="font-semibold text-text-primary mb-2">Next Steps:</h5>
            <ul className="space-y-2">
              {recommendation?.nextSteps?.map((step, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <Icon name="ArrowRight" size={16} color="var(--color-brand-primary)" />
                  <span className="text-text-secondary">{step}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="flex space-x-4">
          <Button
            variant="outline"
            onClick={handleReset}
            iconName="RotateCcw"
            iconPosition="left"
            className="flex-1"
          >
            Start Over
          </Button>
          <Button
            variant="default"
            className="bg-brand-primary hover:bg-brand-primary/90 flex-1"
            iconName="Calendar"
            iconPosition="right"
          >
            Schedule Consultation
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-text-primary">
            Service Configurator
          </h3>
          <span className="text-sm text-text-secondary">
            Step {currentStep + 1} of {steps?.length}
          </span>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-brand-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps?.length) * 100}%` }}
          />
        </div>
      </div>
      <div className="mb-8">
        <h4 className="text-lg font-semibold text-text-primary mb-4">
          {currentStepData?.title}
        </h4>
        
        <Select
          options={currentStepData?.options}
          value={selections?.[currentStepData?.key]}
          onChange={(value) => setSelections(prev => ({
            ...prev,
            [currentStepData?.key]: value
          }))}
          placeholder="Select an option..."
          className="mb-4"
        />
      </div>
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 0}
          iconName="ArrowLeft"
          iconPosition="left"
        >
          Back
        </Button>
        
        <Button
          variant="default"
          onClick={handleNext}
          disabled={!isStepComplete}
          className="bg-brand-primary hover:bg-brand-primary/90"
          iconName={currentStep === steps?.length - 1 ? "Target" : "ArrowRight"}
          iconPosition="right"
        >
          {currentStep === steps?.length - 1 ? "Get Recommendation" : "Next"}
        </Button>
      </div>
    </div>
  );
};

export default ServiceConfigurator;